import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl';
import { DATE_FORMAT_RU } from 'constants/date';
import {
  HIT_STATUS,
  MAP_ENTITY_PARAM_VALUES,
  mapEntityParams,
  NOT_RELEVANT,
} from 'constants/entities';
import { ENTITY_PREVIEW_MODAL } from 'constants/modals';
import { useAppDispatch, useAppSelector } from 'hooks';
import { IMediaFile } from 'interfaces';
import { mapEntitiesActions } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice';
import { updateMapEntityThunk } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/actions';
import { entitiesSelector } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/selectors';
import { modalsActions } from 'store/slices/service/modalsSlice';
import { MapEntity, TPosition } from 'types';
import { PredefinedTemplate } from 'types/entities';

import { ObjectPreviewHeader } from 'components/Map/ObjectPreview/ObjectPreviewHeader';
import { ObjectPreviewTable } from 'components/Map/ObjectPreview/ObjectPreviewTable';
import { MediaGalleryModal } from 'components/MediaGalleryModal';
import {
  copyShareLinkToBuffer,
  formatWgsCoords,
  formatXYCoords,
  getEntityCoordinates,
  getEntityParametersTitleToIdMap,
  getFormattedDate,
} from 'utils';
import {
  changeMapEntityParam,
  exportEntityWrapper,
  getMapObjectEntityValues,
  getSelectOptionsByMapEntityParam,
} from 'utils';
import { convertWsgToSk42 } from 'utils/converterUtils';

import { ObjectPreviewContent } from '../ObjectPreview/ObjectPreviewContent';
import { ObjectPreviewFooter } from '../ObjectPreview/ObjectPreviewFooter';
import { ObjectPreviewHorizontalTable } from '../ObjectPreview/ObjectPreviewHorizontalTable';
import { ObjectPreviewPopup } from '../ObjectPreview/ObjectPreviewPopup';
import { ObjectPreviewTitle } from '../ObjectPreview/ObjectPreviewTitle';

interface EntityPreviewProps {
  entity: MapEntity;
  mapObjectTemplate: PredefinedTemplate;
  onClose: () => void;
}

export const EntityPreview: FC<EntityPreviewProps> = ({
  entity,
  mapObjectTemplate,
  onClose,
}) => {
  const mapRef = useMap();
  const [mapEntity, setMapEntity] = useState<MapEntity>(entity);
  const [sk42Coordinates, setSk42Coordinates] = useState({ x: 0, y: 0, h: 0 });
  const [isMediaGalleryOpen, setMediaGalleryOpen] = useState(false);
  const popupContentRef = useRef<HTMLDivElement>(null);
  const isMapEntityModifiedRef = useRef(false);

  const entities = useAppSelector(entitiesSelector);

  const dispatch = useAppDispatch();

  const parametersIdTitleMap = useMemo(
    () =>
      getEntityParametersTitleToIdMap(
        mapObjectTemplate.parameters,
        MAP_ENTITY_PARAM_VALUES
      ),
    [mapObjectTemplate.parameters]
  );

  const mapEntityValues = getMapObjectEntityValues(
    parametersIdTitleMap,
    mapEntity.entity
  );

  const name = mapEntityValues.name || 'Нет имени';
  const status = mapEntityValues.status || 'Неизвестен';
  const geometry = mapEntityValues.geometry || {
    type: 'Point',
    coordinates: [0, 0],
  };
  const id = mapEntity.entity.id;

  const date =
    getFormattedDate(mapEntityValues.date, DATE_FORMAT_RU) || 'Нет даты';

  const dateAdded =
    getFormattedDate(entity.entity.createdAt, DATE_FORMAT_RU) || 'Нет даты';

  const description = mapEntityValues.description || '';
  const media = mapEntityValues.media || [];
  const hasMediaAndDescription = media.length && description;

  const [mediaFiles, setMediaFiles] = useState<IMediaFile[]>(media);

  const statusSelectOptions = useMemo(
    () =>
      getSelectOptionsByMapEntityParam(
        mapEntityParams.STATUS,
        mapObjectTemplate
      ),
    [mapObjectTemplate]
  );

  const handleSaveEntity = async (mapEntity: MapEntity) =>
    await dispatch(updateMapEntityThunk(mapEntity.entity));

  const handleSelectStatus = (value: any) => {
    const tempEntity = changeMapEntityParam(
      mapEntity,
      parametersIdTitleMap,
      mapEntityParams.STATUS,
      value
    );
    const newEntity =
      value === HIT_STATUS
        ? changeMapEntityParam(
            tempEntity,
            parametersIdTitleMap,
            mapEntityParams.RELEVANCE,
            NOT_RELEVANT
          )
        : tempEntity;
    handleSaveEntity(newEntity);
    setMapEntity(newEntity);
  };

  const wgsCoordinates: TPosition = useMemo(() => {
    return getEntityCoordinates(geometry);
  }, [geometry]);

  const handleUpdateMapEntity = async () => {
    if (!isMapEntityModifiedRef.current) {
      return;
    }

    const updatedEntity = {
      ...mapEntity.entity,
      parameters: {
        ...mapEntity.entity.parameters,
        [parametersIdTitleMap[mapEntityParams.MEDIA]]: {
          ...mapEntity.entity.parameters[
            parametersIdTitleMap[mapEntityParams.MEDIA]
          ],
          value: mediaFiles,
        },
      },
    };

    setMapEntity({ ...mapEntity, entity: updatedEntity });
    await dispatch(updateMapEntityThunk(updatedEntity));
  };

  const handleMediaFileDrop = (media: IMediaFile[]) => {
    isMapEntityModifiedRef.current = true;

    setMediaFiles(media);
  };

  const handleDeleteMediaFile = (index: number) => {
    const deletedMediaFile = mediaFiles[index];

    if (!deletedMediaFile) {
      return;
    }

    isMapEntityModifiedRef.current = true;

    setMediaFiles((prevState) =>
      prevState.filter((mediaFile) => mediaFile.url !== deletedMediaFile.url)
    );
  };

  const handleMediaModalClose = async () => {
    await handleUpdateMapEntity();

    isMapEntityModifiedRef.current = false;

    setMediaGalleryOpen(false);
  };

  const dateRows = [
    {
      label: 'Внесено',
      value: dateAdded,
    },
    {
      label: 'Обнаружено',
      value: date,
    },
  ];

  const statusRow = [
    {
      label: 'Статус ОП',
      value: status,
      selectProps: {
        options: statusSelectOptions,
        handler: handleSelectStatus,
      },
    },
  ];

  const locationRows = [
    {
      label: 'WGS',
      value: formatWgsCoords(wgsCoordinates[1], wgsCoordinates[0]),
      copyable: true,
    },
    {
      label: 'CK42',
      value: formatXYCoords(sk42Coordinates.x, sk42Coordinates.y),
      copyable: true,
    },
  ];

  const mediaGalleryActions = [
    {
      title: 'Удалить',
      onClick: (_: number, currentMediaIndex: number) =>
        handleDeleteMediaFile(currentMediaIndex),
    },
  ];

  useEffect(() => {
    const getSk42Coordinates = async () => {
      const sk42Coordinates = await convertWsgToSk42(wgsCoordinates);

      if (sk42Coordinates) {
        setSk42Coordinates(sk42Coordinates);
      }
    };

    getSk42Coordinates();
  }, [wgsCoordinates]);

  const handleShare = () => copyShareLinkToBuffer(entity.entity.id);

  const handleExport = async () =>
    await exportEntityWrapper(mapEntity.entity.id);

  const handleRemove = () => {
    const deselectedEntity = entities[id];

    dispatch(
      mapEntitiesActions.setEntities({
        ...entities,
        [id]: {
          ...deselectedEntity,
          state: { ...deselectedEntity.state, active: false },
        },
      })
    );

    dispatch(modalsActions.removeModal(ENTITY_PREVIEW_MODAL));
  };

  const fitPopupBounds = useCallback((coordinates: TPosition) => {
    const map = mapRef.current?.getMap?.();
    const popup = popupContentRef.current;

    if (!map || !popup) {
      return;
    }

    const popupHeight = popup.clientHeight;
    const popupWidth = popup.clientWidth;

    const pixelCoordinates = map.project(coordinates);
    const isPopupOverflowed = pixelCoordinates.y - popupHeight < 0;
    if (isPopupOverflowed) {
      const aspectRatio = popupHeight / popupWidth;
      const verticalOffset = Math.min(200, aspectRatio * 105);

      const swPixel: [number, number] = [
        pixelCoordinates.x - popupWidth / 2,
        pixelCoordinates.y + popupHeight / 2 - verticalOffset,
      ];

      const nePixel: [number, number] = [
        pixelCoordinates.x + popupWidth / 2,
        pixelCoordinates.y - popupHeight / 2 - verticalOffset,
      ];

      const swCoordinates = map.unproject(swPixel);
      const neCoordinates = map.unproject(nePixel);

      map.fitBounds([swCoordinates, neCoordinates], { padding: 10 });
    }
  }, []);

  useEffect(() => {
    // TODO. Временное решение для превью с медиа
    const timeout = setTimeout(() => {
      fitPopupBounds(wgsCoordinates);
    }, 100);

    return () => clearTimeout(timeout);
  }, [wgsCoordinates, fitPopupBounds]);

  return (
    <>
      <ObjectPreviewPopup
        longitude={wgsCoordinates[0]}
        latitude={wgsCoordinates[1]}
        offset={30}
        onClose={onClose}
        maxWidth={hasMediaAndDescription ? '740px' : '390px'}
        style={{ width: hasMediaAndDescription ? '740px' : '390px' }}
      >
        <div ref={popupContentRef}>
          <ObjectPreviewHeader name={name} id={id} />
          {hasMediaAndDescription ? (
            <ObjectPreviewHorizontalTable
              rows={dateRows}
              firstCellWidth="90%"
            />
          ) : (
            <ObjectPreviewTable rows={dateRows} firstCellWidth="90%" />
          )}
          {(media || description) && (
            <ObjectPreviewContent
              media={media}
              setMediaGalleryOpen={setMediaGalleryOpen}
              description={description}
            />
          )}
          <ObjectPreviewTable
            rows={statusRow}
            firstCellWidth={hasMediaAndDescription ? '370px' : '30%'}
          />
          <ObjectPreviewTitle>Расположение</ObjectPreviewTitle>
          {hasMediaAndDescription ? (
            <ObjectPreviewHorizontalTable
              rows={locationRows}
              firstCellWidth="30%"
            />
          ) : (
            <ObjectPreviewTable rows={locationRows} firstCellWidth="30%" />
          )}
          <ObjectPreviewFooter
            handleShare={handleShare}
            handleExport={handleExport}
            handleRemove={handleRemove}
          />
        </div>
      </ObjectPreviewPopup>
      {isMediaGalleryOpen && (
        <MediaGalleryModal
          mediaFiles={mediaFiles}
          actions={mediaGalleryActions}
          containerClassName="!absolute"
          isDraggable
          onSlideDrop={handleMediaFileDrop}
          onClose={handleMediaModalClose}
        />
      )}
    </>
  );
};
